/**
 * @fileoverview Redux reducer for Click&Pledge donation feature.
 * Manages the state for all Click&Pledge related functionality including
 * donation forms, payment processing, recurring donations, and campaign data.
 */


import { PersistenceRegistry, ReducerRegistry } from '../base/redux';

import {
    ACCOUNTMODE, AMOUNTSECTION, CAMPAIGNRAISEDDETAILS, CHARGEDATEDATA, CLEAR_FORM_API_CACHE, CNP_PAYQUICKLY_DATA, CONFEEORDERITEM, CONVENIENCEFEEAMOUNT, CONVENIENCEFEESECTION,
    CONVENIENCEFEESELECTED, DEFAULTDONATIONAMOUNT, DONATIONAMOUNTINDEX, DONATIONAMOUNTSELECTED, DONATIONFORMDETAILS, DONATIONITEMS, DONATIONORDERITEMS,
    DONATIONSTREAM, DONATION_INFO_DATA, DONATION_MAREQUE, FINALDONATIONAMOUNTERROR, FINALDONATIONAMT, FORMGUID, FORMSETTINGS, FORM_INSTANCE_DATA, FORM_PUBLISH_STATUS, GIVENTID, GIVENT_TOKEN,
    HASINSTALLMENTERROR, HASLESSINSTALLMENTAMT, INSTALLEMENTSELECTED, INSTALLMENTCHARGE,
    INSTALLMENTERRORMSG, ISDONATIONAMOUNTEMPTY, ISDONATIONDONE, ISEVENTEXPIRED, ISINDEFINITERECURRING, ISINSTALLMENTTOUCHED, ISPAYSECTOUCHED,
    IS_ENABLED, IS_MARQUEE_CLOSED, LASTSTREAMTIME, MEETFORMDATA, MEET_DATA, MULTIDONATIONERR, MULTIDONATIONORDERITEMS, MULTIDONATIONVALS,
    MULTIPAYTOTALTAXDEDUCT, NOOFPERIODICITY, NORECURRINGOPTIONAVAILABLE, OTHERAMOUNTMINMAXERROR, PAYMENTERR, PAYMENTORDERITEM, PAYMENTORDERNO, PAYMENTSECTION, PAYMENTTYPEMETHOD,
    PERIODICITYENDDATE, RECURRINGMETHOD, RECURRINGSECTION, RECURRINGSELECTION, RENDER_POPUP_OVERLAY, RESET_CLICKANDPLEDGE, SAVE_FORM_GUID_DATA, SET_CNP_API_CACHE, SHOWASANONYMOUS,
    SHOWHIDEFORM, SHOWONETIMEPAYOPTION, STREAMOPTIONS, STREAM_INFO_DATA, TOTALDONATIONAMOUNT, TOUCHEDFIELDS, USERACCOUNTINFO, USERID, USER_FORM_DATA, VALIDATIONSECTION,
    WALLET_CONTACT_OVERRIDE, WALLET_PAYMENT_RETURN
} from './actionTypes';


/**
 * Default state for the Click&Pledge feature.
 * Contains all initial values for donation forms, payment processing,
 * recurring donations, installments, convenience fees, and campaign data.
 */
const DEFAULT_STATE = {
    meetData: {},
    giventToken: undefined,
    clickAndPledgeUrl: undefined,
    userId: undefined,
    marequeData: {},
    isEnabled: false,
    useraccountinfo: {},
    formGUID: '',
    formSettings: {},
    giventID: 0,
    accountMode: {},
    chargeDateData: {},
    isEventExpired: '',
    meetFormData: [],
    donationFormDetails: {},
    cnpPayQuicklyData: {},
    renderPopupOverlay: {
        isVisible: false,
        popupWindow: null
    },

    // Recurring & Amount Sections
    recurringSection: {},
    amountSection: {},
    recurringSelection: '',
    recurringMethod: '',
    paymentTypeMethod: '',
    donationAmountSelected: '',
    donationAmountIndex: null,
    donationItems: [],
    noofperiodicity: '',
    periodicityEndDate: '',
    showoneTimePayOption: false,
    noRecurringOptionAvailable: false,

    // Payment
    paymentSection: [],
    paymentOrderNo: '',
    paymentErr: false,
    touchedfields: [],

    // Installments
    installementSelected: '',
    hasInstallmentError: false,
    haslessInstallmentAmt: false,
    installmentErrorMsg: '',
    installmentCharge: '',
    isInstallmentTouched: false,
    isIndefiniteRecurring: false,

    // Convenience Fee
    convenienceFeeSection: [],
    convenienceFeeAmount: '',
    convenienceFeeSelected: true,

    // Donation Processing
    finalDonationAmt: '',
    finalDonationAmountError: false,
    isDonationAmountEmpty: false,
    isDonationDone: false,
    defaultDonationAmount: 0,
    otherAmountMinMaxError: false,
    validationSection: {
        AmountSection: false,
        PaymentSection: false
    },
    showhideForm: true,
    isPaySecTouched: false,
    showAsAnonymous: false,

    // Order Items
    donationOrderItems: {},
    conFeeOrderItem: {},
    paymentOrderItem: {},
    multiDonationOrderItems: [],
    multiPayTotalTaxDeduct: 0,
    multiDonationErr: '',
    multiDonationVals: {},

    // Campaign & Stream
    campaignRaisedDetails: {},
    streamOptions: {},
    streamcheckboxes: {},
    donationStream: [],
    totalDonationAmount: {},
    lastStreamTime: {},

    // Redux-based data storage (replaces SimpleDB)
    formInstanceData: {},
    formGuidData: {},
    userFormData: {},
    formPublishStatus: false,
    donationInfoData: [],
    streamInfoData: [],

    // Marquee visibility
    isMarqueeClosed: false,

    // Latest wallet-payment deep-link return ({ url, ts }) relayed from
    // appNavigate when a PayPal / Amazon Pay approval re-enters the app.
    walletPaymentReturn: null,

    // Cached API responses for the session
    apiCache: {},

    // Contact details the donor typed into the wallet prompt, plus a signature
    // of the Settings profile they were filled in against. The only key of this
    // feature that outlives the app process - see the PersistenceRegistry
    // registration below.
    walletContactOverride: undefined
};

/**
 * Persists only the wallet contact override. Everything else in this feature is
 * campaign data that has to come back from the API, so whitelisting one key
 * keeps a stale form from being restored over a fresh one.
 */
PersistenceRegistry.register('features/clickandpledge', {
    walletContactOverride: true
}, DEFAULT_STATE);

/**
 * Click&Pledge reducer function.
 * Handles all state updates for the Click&Pledge donation feature.
 *
 * @param {Object} state - Current state, defaults to DEFAULT_STATE
 * @param {Object} action - Redux action with type and payload
 * @returns {Object} Updated state
 */
ReducerRegistry.register('features/clickandpledge', (state = DEFAULT_STATE, action) => {

    switch (action.type) {
    // Basic Info
    case IS_ENABLED: return {
        ...state,
        isEnabled: action.payload
    };
    case GIVENT_TOKEN: return {
        ...state,
        giventToken: action.payload
    };
    case CNP_PAYQUICKLY_DATA: return {
        ...state,
        cnpPayQuicklyData: action.payload
    };
    case USERID: return {
        ...state,
        userId: action.payload
    };
    case RENDER_POPUP_OVERLAY: return {
        ...state,
        renderPopupOverlay: {
            isVisible: action.payload,
            popupWindow: action.popupWindow || null
        }
    };
    case MEET_DATA: return {
        ...state,
        meetData: action.payload
    };
    case USERACCOUNTINFO: return {
        ...state,
        useraccountinfo: action.payload
    };
    case FORMGUID: return {
        ...state,
        formGUID: action.payload,
        clickAndPledgeUrl: action.payload || undefined
    };
    case FORMSETTINGS: return {
        ...state,
        formSettings: action.payload
    };
    case GIVENTID: return {
        ...state,
        giventID: action.payload
    };
    case ACCOUNTMODE: return {
        ...state,
        accountMode: action.payload
    };
    case CHARGEDATEDATA: return {
        ...state,
        chargeDateData: action.payload
    };
    case ISEVENTEXPIRED: return {
        ...state,
        isEventExpired: action.payload
    };
    case MEETFORMDATA: return {
        ...state,
        meetFormData: action.payload
    };
    case DONATIONFORMDETAILS: return {
        ...state,
        donationFormDetails: action.payload
    };
    case DONATION_MAREQUE: return {
        ...state,
        marequeData: action.payload
    };

        // Recurring + Amount
    case RECURRINGSECTION: return {
        ...state,
        recurringSection: action.payload
    };
    case AMOUNTSECTION: return {
        ...state,
        amountSection: action.payload
    };
    case RECURRINGSELECTION: return {
        ...state,
        recurringSelection: action.payload
    };
    case RECURRINGMETHOD: return {
        ...state,
        recurringMethod: action.payload
    };
    case PAYMENTTYPEMETHOD: return {
        ...state,
        paymentTypeMethod: action.payload
    };
    case DONATIONAMOUNTSELECTED: return {
        ...state,
        donationAmountSelected: action.payload
    };
    case DONATIONAMOUNTINDEX: return {
        ...state,
        donationAmountIndex: action.payload
    };
    case DONATIONITEMS: return {
        ...state,
        donationItems: action.payload
    };
    case NOOFPERIODICITY: return {
        ...state,
        noofperiodicity: action.payload
    };
    case PERIODICITYENDDATE: return {
        ...state,
        periodicityEndDate: action.payload
    };
    case SHOWONETIMEPAYOPTION: return {
        ...state,
        showoneTimePayOption: action.payload
    };
    case NORECURRINGOPTIONAVAILABLE: return {
        ...state,
        noRecurringOptionAvailable: action.payload
    };

        // Payment
    case PAYMENTSECTION: return {
        ...state,
        paymentSection: action.payload
    };
    case PAYMENTORDERNO: return {
        ...state,
        paymentOrderNo: action.payload
    };
    case PAYMENTERR: return {
        ...state,
        paymentErr: action.payload
    };
    case TOUCHEDFIELDS: return {
        ...state,
        touchedfields: action.payload
    };

        // Installments
    case INSTALLEMENTSELECTED: return {
        ...state,
        installementSelected: action.payload
    };
    case HASINSTALLMENTERROR: return {
        ...state,
        hasInstallmentError: action.payload
    };
    case HASLESSINSTALLMENTAMT: return {
        ...state,
        haslessInstallmentAmt: action.payload
    };
    case INSTALLMENTERRORMSG: return {
        ...state,
        installmentErrorMsg: action.payload
    };
    case INSTALLMENTCHARGE: return {
        ...state,
        installmentCharge: action.payload
    };
    case ISINSTALLMENTTOUCHED: return {
        ...state,
        isInstallmentTouched: action.payload
    };
    case ISINDEFINITERECURRING: return {
        ...state,
        isIndefiniteRecurring: action.payload
    };

        // Convenience Fee
    case CONVENIENCEFEESECTION: return {
        ...state,
        convenienceFeeSection: action.payload
    };
    case CONVENIENCEFEEAMOUNT: return {
        ...state,
        convenienceFeeAmount: action.payload
    };
    case CONVENIENCEFEESELECTED: return {
        ...state,
        convenienceFeeSelected: action.payload
    };

        // Donation Processing
    case FINALDONATIONAMT: return {
        ...state,
        finalDonationAmt: action.payload
    };
    case FINALDONATIONAMOUNTERROR: return {
        ...state,
        finalDonationAmountError: action.payload
    };
    case ISDONATIONAMOUNTEMPTY: return {
        ...state,
        isDonationAmountEmpty: action.payload
    };
    case ISDONATIONDONE: return {
        ...state,
        isDonationDone: action.payload
    };
    case DEFAULTDONATIONAMOUNT: return {
        ...state,
        defaultDonationAmount: action.payload
    };
    case OTHERAMOUNTMINMAXERROR: return {
        ...state,
        otherAmountMinMaxError: action.payload
    };
    case VALIDATIONSECTION: return {
        ...state,
        validationSection: action.payload
    };
    case SHOWHIDEFORM: return {
        ...state,
        showhideForm: action.payload
    };
    case ISPAYSECTOUCHED: return {
        ...state,
        isPaySecTouched: action.payload
    };
    case SHOWASANONYMOUS: return {
        ...state,
        showAsAnonymous: action.payload
    };

        // Order Items
    case DONATIONORDERITEMS: return {
        ...state,
        donationOrderItems: action.payload
    };
    case CONFEEORDERITEM: return {
        ...state,
        conFeeOrderItem: action.payload
    };
    case PAYMENTORDERITEM: return {
        ...state,
        paymentOrderItem: action.payload
    };
    case MULTIDONATIONORDERITEMS: return {
        ...state,
        multiDonationOrderItems: action.payload
    };
    case MULTIPAYTOTALTAXDEDUCT: return {
        ...state,
        multiPayTotalTaxDeduct: action.payload
    };
    case MULTIDONATIONERR: return {
        ...state,
        multiDonationErr: action.payload
    };
    case MULTIDONATIONVALS: return {
        ...state,
        multiDonationVals: action.payload
    };

        // Campaign + Stream
    case CAMPAIGNRAISEDDETAILS: return {
        ...state,
        campaignRaisedDetails: action.payload
    };
    case STREAMOPTIONS: return {
        ...state,
        streamOptions: action.payload,
        streamcheckboxes: action.payload
    };
    case DONATIONSTREAM: return {
        ...state,
        donationStream: action.payload
    };
    case TOTALDONATIONAMOUNT: return {
        ...state,
        totalDonationAmount: action.payload
    };
    case LASTSTREAMTIME: return {
        ...state,
        lastStreamTime: action.payload
    };

        // Redux-based data storage (replaces SimpleDB API calls)
    case FORM_INSTANCE_DATA: return {
        ...state,
        formInstanceData: action.payload
    };
    case SAVE_FORM_GUID_DATA: return {
        ...state,
        formGuidData: action.payload
    };
    case USER_FORM_DATA: return {
        ...state,
        userFormData: action.payload
    };
    case FORM_PUBLISH_STATUS: return {
        ...state,
        formPublishStatus: action.payload
    };
    case DONATION_INFO_DATA: return {
        ...state,
        donationInfoData: action.payload
    };
    case STREAM_INFO_DATA: return {
        ...state,
        streamInfoData: action.payload
    };

    case IS_MARQUEE_CLOSED: return {
        ...state,
        isMarqueeClosed: action.payload
    };

    case WALLET_PAYMENT_RETURN: return {
        ...state,
        walletPaymentReturn: action.payload
    };

    case SET_CNP_API_CACHE: return {
        ...state,
        apiCache: {
            ...state.apiCache,
            [action.cacheKey]: action.data
        }
    };

    case CLEAR_FORM_API_CACHE: {
        const preservedCache = {};

        Object.keys(state.apiCache || {}).forEach(key => {
            if (key.startsWith('registration:') || key.startsWith('getform:')) {
                preservedCache[key] = state.apiCache[key];
            }
        });

        return {
            ...state,
            apiCache: preservedCache
        };
    }

    // Reset to default state but preserve donation amount, contact info, and recurring selection
    case WALLET_CONTACT_OVERRIDE:
        return {
            ...state,
            walletContactOverride: action.payload
        };

    case RESET_CLICKANDPLEDGE:
        return {
            ...DEFAULT_STATE,

            // Survives the reset: it belongs to the donor, not to the
            // Click&Pledge session being torn down.
            walletContactOverride: state.walletContactOverride
        };

    }


    return state;
});
